import React, { useEffect, useState, useMemo } from "react";
import { Button, Modal, Segmented, Select, Space, Typography, message } from "antd";
import moment from "moment";
import { SeriesEvent, SeriesDropdownValue } from './types';
import { generateEventId } from './utils';
import { AppstoreOutlined, BarsOutlined } from "@ant-design/icons";
import { LabelText, ModeSegmented } from "./StyledComponents";
import { SegmentedValue } from "antd/es/segmented";
const { Option } = Select;
const { Title } = Typography;

// Utility to create time options in 30-min intervals
const generateTimeSlots = (start = 0, end = 24) => {
    const slots = [];
    for (let h = start; h < end; h++) {
      slots.push(`${String(h).padStart(2, "0")}:00`);
      slots.push(`${String(h).padStart(2, "0")}:30`);
    }
    return slots;
  };
  

const timeOptions = generateTimeSlots();

type TimeModalProps = {
  visible: boolean;
  seriesDropdownValues: SeriesDropdownValue[];
  initialData: SeriesEvent | null;
  onClose: (data: SeriesEvent | null) => void;
  onDelete: (oldEventID: string) => void;
  eventMode: 'Series' | 'Paid Programming';
  setEventMode: (mode: 'Series' | 'Paid Programming') => void;
  mediaList: any[];
};

const TimeModal = ({ visible, seriesDropdownValues, initialData, onClose, onDelete, eventMode, setEventMode, mediaList }: TimeModalProps) => {
    // Separate state for Series and Paid Programming selections
    const [seriesSelectedId, setSeriesSelectedId] = useState<string | null>(null);
    const [mediaSelectedId, setMediaSelectedId] = useState<string | null>(null);
    
    const [oldEventId, setOldEventId] = useState<string | null>(initialData?.id || null);
    const [startHourString, setStartHourString] = useState<string | null>(initialData?.startHourString || null);
    const [endHourString, setEndHourString] = useState<string | null>(initialData?.endHourString || null);
    
    // Get the current selection based on event mode
    const currentSelectedId = eventMode === 'Series' ? seriesSelectedId : mediaSelectedId;
    
    // Handler to update the appropriate selection based on mode
    const handleSelectionChange = (value: string | null) => {
      if (eventMode === 'Series') {
        setSeriesSelectedId(value);
      } else {
        setMediaSelectedId(value);
      }
    };
  
    const handleSubmit = () => {
      if(initialData === null) {
        message.error(`Please select a ${eventMode === 'Series' ? 'series' : 'media'}`);
        return;
      }
      if (!currentSelectedId) {
        message.error(`Please select a ${eventMode === 'Series' ? 'series' : 'media'}`);
        return;
      }
      const newStart = moment(initialData.start)
        .set({ hour: Number(startHourString?.split(':')[0]), minute: Number(startHourString?.split(':')[1]) })
        .toDate();
      const newEnd = moment(initialData.end)
        .set({ hour: Number(endHourString?.split(':')[0]), minute: Number(endHourString?.split(':')[1]) })
        .toDate();
      onClose({
        id: generateEventId(currentSelectedId || "", newStart, newEnd),
        seriesId: currentSelectedId || "",
        oldEventId: oldEventId || "",
        start: newStart,
        end: newEnd,
        startHourString : startHourString || "",
        endHourString : endHourString || "",
        eventType: eventMode,
        mediaId: eventMode === 'Paid Programming' ? currentSelectedId : undefined,
      });
    };
  
    useEffect(() => {
      if (initialData) {
        // Determine if initialData.seriesId is a series ID or media ID
        const isSeriesId = seriesDropdownValues.some(item => item.id === initialData.seriesId);
        const isMediaId = mediaList.some(item => (item.id || item.title) === initialData.seriesId);
        
        // Set the appropriate selection based on what type it is
        if (isSeriesId) {
          setSeriesSelectedId(initialData.seriesId);
        } else if (isMediaId) {
          setMediaSelectedId(initialData.seriesId);
        } else {
          // Fallback: set based on current mode if we can't determine
          if (eventMode === 'Series') {
            setSeriesSelectedId(initialData.seriesId);
          } else {
            setMediaSelectedId(initialData.seriesId);
          }
        }
        
        setOldEventId(initialData.id);
        setStartHourString(initialData.startHourString);
        setEndHourString(initialData.endHourString);
      }
      // Don't reset selections when initialData is null - preserve user's previous selections
    }, [initialData, seriesDropdownValues, mediaList]);
    
    const handleDuplicate = () => {
      if(initialData === null) {
        message.error(`Please select a ${eventMode === 'Series' ? 'series' : 'media'}`);
        return;
      }
      if (!currentSelectedId) {
        message.error(`Please select a ${eventMode === 'Series' ? 'series' : 'media'}`);
        return;
      }
      const dupEventStartMoment = moment(initialData.start)
        .set({ hour: Number(startHourString?.split(':')[0]), minute: Number(startHourString?.split(':')[1]) })
        .add(15, 'minute');
      let dupEventEndMoment = moment(initialData.end)
        .set({ hour: Number(endHourString?.split(':')[0]), minute: Number(endHourString?.split(':')[1]) })
        .add(15, 'minute');

      // Cap at end of day when +15min would roll past midnight (e.g. 23:59 → 00:14)
      if (dupEventEndMoment.isAfter(dupEventStartMoment.clone().endOf('day'))) {
        dupEventEndMoment = dupEventStartMoment.clone().endOf('day');
      }

      const dupEventStart = dupEventStartMoment.toDate();
      const dupEventEnd = dupEventEndMoment.toDate();
      const dupEventStartHourString = dupEventStartMoment.format('HH:mm');
      // endOf('day') formats as 23:59, which matches the end-time dropdown option
      const dupEventEndHourString = dupEventEndMoment.format('HH:mm');
      const dupEventId = generateEventId(currentSelectedId || "", dupEventStart, dupEventEnd);

      onClose({
        id: dupEventId,
        seriesId: currentSelectedId || "",
        oldEventId: dupEventId,
        start: dupEventStart,
        end: dupEventEnd,
        startHourString : dupEventStartHourString,
        endHourString : dupEventEndHourString,
        eventType: eventMode,
        mediaId: eventMode === 'Paid Programming' ? currentSelectedId : undefined,
      });
    }

    const handleCancel = () => {
      onClose(null); // or pass empty data if needed
    };
  
    const handleDelete = () => {
      onDelete(oldEventId || "");
    };

    const handleEventModeChange = (mode: string) => {
      setEventMode(mode as 'Series' | 'Paid Programming');
      // Don't reset selections - they are preserved separately for each mode
    }

    // Format mediaList for dropdown options (deduplicate by title)
    const mediaOptions = useMemo(() => {
      if (eventMode !== 'Paid Programming' || !mediaList || mediaList.length === 0) {
        return [];
      }
      return Array.from(
        new Map(mediaList.map((item: any) => [item.title, item])).values()
      ).map((item: any) => ({
        value: item.title,
        label: item.title,
      }));
    }, [mediaList, eventMode]);

    return (
      <Modal
        open={visible}
        title="Event Details"
        onOk={handleSubmit}
        onCancel={handleCancel}
        okButtonProps={{ disabled: !currentSelectedId || !startHourString || !endHourString }}
        footer={[
          <Button key='delete' type='primary' danger onClick={handleDelete}>
            Delete
          </Button>,
          <Button key="duplicate" onClick={handleDuplicate}>
            Duplicate
          </Button>,
          <Button key="back" onClick={handleCancel}>
            Return
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            Submit
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }}> 
          <ModeSegmented
            options={[
              { label: 'Series', value: 'Series' },
              { label: 'Paid Programming', value: 'Paid Programming' },
            ]}
            value={eventMode}
            onChange={(mode: SegmentedValue) => handleEventModeChange(mode as string)}
            style={{ margin: "10px 0" }}
          />   
          <LabelText inline>Select {eventMode === 'Series' ? 'Series' : 'Media'}</LabelText>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select item"
            value={currentSelectedId}
            onChange={handleSelectionChange}
            loading={eventMode === 'Series' ? seriesDropdownValues.length === 0 : mediaList.length === 0}
            style={{ width: "100%" }}
            dropdownStyle={{ maxWidth: "100%" }}
            options={eventMode === 'Series' 
              ? seriesDropdownValues.map((item) => ({
                  value: item.id,
                  label: item.titles,
                }))
              : mediaOptions
            }
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
          <LabelText inline>Start Time</LabelText>
          <Select
            placeholder="Start Time"
            value={startHourString}
            onChange={setStartHourString}
          >
            {timeOptions.map((t) => (
              <Option key={t} value={t}>
                {t}
              </Option>
            ))}
          </Select>
          <LabelText inline>End Time</LabelText>
          <Select
            placeholder="End Time"
            value={endHourString}
            onChange={setEndHourString}
          >
            {[...timeOptions, '23:59'].map((t) => (
              <Option key={t} value={t}>
                {t}
              </Option>
            ))}
          </Select>
        </Space>
      </Modal>
    );
  };

export default TimeModal;
